<template>
  <div class="d-flex w-100 align-items-center">
    <DatePicker
      ref="picker"
      v-model="rangeInternal"
      range
      :range-separator="rangeSeparator"
      :format="displayFormat"
      value-type="date"
      :placeholder="rangePlaceholder"
      :not-after="endMax"
      :editable="true"
      :input-attr="inputAttrs"
      class="st-form-picker w-100"
      clearable
      :lang="datePickerLang"
      @change="onChange"
      @clear="onClear"
      @input-error="onInputError"
    />
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

import ro from 'vue2-datepicker/locale/ro';
import en from 'vue2-datepicker/locale/en';

import { createTextMaskInputElement } from 'text-mask-core/dist/textMaskCore';

import i18nService from '@/shared/services/i18n.service';
import {
  buildDisplayFormat,
  externalToDate,
  dateToExternal,
  buildRangeTextMask,
  isMaskedRangeEmpty,
  isMaskedValueComplete,
  isValidMaskedDateString,
  isValidMaskedRangeString,
  getStartPart,
  getEndPart,
  buildDisplayFormatForPlaceholder
} from '@/shared/utils/datePicker.helper';

export default {
  name: 'StDateRange',
  components: { DatePicker },
  data() {
    return {
      locale: i18nService.getActiveLanguage(),
      maskController: null,
      maskInputEl: null,
      maskInputHandler: null,
      maskBlurHandler: null,
    };
  },
  props: {
    value: { type: Object, required: true },
    startField: { type: String, required: true },
    endField: { type: String, required: true },
    formatOptions: {
      type: Object,
      default: () => ({
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        separator: '.',
      }),
    },
    endMax: { type: Date, default: null },
    rangeSeparator: { type: String, default: ' ~ ' },
    placeholder: { type: String, default: '' },
  },
  computed: {
    displayFormat() {
      return buildDisplayFormat(this.formatOptions);
    },
    datePickerLang() {
      const l = (this.locale || '').toLowerCase();
      return l.startsWith('ro') ? ro : en;
    },
    rangePlaceholder() {
      const dateHint = `${buildDisplayFormatForPlaceholder(this.formatOptions)} ~ ${buildDisplayFormatForPlaceholder(this.formatOptions)}`;
      return this.placeholder.trim() ? `${this.placeholder} (${dateHint})` : dateHint;
    },
    inputAttrs() {
      return {
        name: `${this.startField}_${this.endField}`,
        title: this.placeholder.trim() ? this.rangePlaceholder : undefined,
      };
    },
    rangeInternal: {
      get() {
        const rawStart = this.value?.[this.startField] || null;
        const rawEnd = this.value?.[this.endField] || null;
        return [externalToDate(rawStart), externalToDate(rawEnd)];
      },
      set(val) {
        const [start, end] = Array.isArray(val) ? val : [null, null];

        this.$emit('input', {
          ...(this.value || {}),
          [this.startField]: dateToExternal(start),
          [this.endField]: dateToExternal(end),
        });
      },
    },
  },
  watch: {
    formatOptions: {
      deep: true,
      handler() {
        this.$nextTick(() => this.attachMask(true));
      },
    },
    endMax() {
      this.$nextTick(() => this.attachMask(true));
    },
    rangeSeparator() {
      this.$nextTick(() => this.attachMask(true));
    },
  },
  mounted() {
    this.attachMask(false);
  },
  beforeDestroy() {
    this.detachMask();
  },
  methods: {
    attachMask(force = false) {
      const pickerVm = this.$refs.picker;
      const pickerEl = pickerVm && pickerVm.$el;
      const input = pickerEl ? pickerEl.querySelector('input') : null;
      
      if (!input) return;
      if (!force && this.maskInputEl === input && this.maskController) return;

      this.detachMask();
      this.maskInputEl = input;
      this.maskController = createTextMaskInputElement({
        inputElement: this.maskInputEl,
        mask: buildRangeTextMask(this.formatOptions, this.rangeSeparator),
        guide: true,
        placeholderChar: '_',
        keepCharPositions: true,
      });

      this.maskController.update(this.maskInputEl.value);
      this.maskInputHandler = (e) => {
        if (!this.maskController || !this.maskInputEl) return;
        this.maskController.update(this.maskInputEl.value);
        const full = this.maskInputEl.value || '';
        const startPart = getStartPart(full).trim();
        const endPart = getEndPart(full).trim();

        if (isMaskedValueComplete(startPart)) {
          const okStart = isValidMaskedDateString(startPart, this.formatOptions, { max: this.endMax || null });
          if (!okStart) return this.blockAndClear(e);
        }

        if (isMaskedValueComplete(endPart)) {
          const okEnd = isValidMaskedDateString(endPart, this.formatOptions, { max: this.endMax || null });
          if (!okEnd) return this.blockAndClear(e);
        }

        if (isMaskedValueComplete(startPart) && isMaskedValueComplete(endPart)) {
          const okRange = isValidMaskedRangeString(full, this.formatOptions, {
            rangeSeparator: this.rangeSeparator,
            endMax: this.endMax || null,
          });
          if (!okRange) return this.blockAndClear(e);
        }
      };
      this.maskBlurHandler = () => {
        if (!this.maskInputEl || !this.maskController) return;

        const full = this.maskInputEl.value || '';
        if (isMaskedRangeEmpty(full)) return;

        const startPart = getStartPart(full).trim();
        const endPart = getEndPart(full).trim();

        if (isMaskedValueComplete(startPart)) {
          const okStart = isValidMaskedDateString(startPart, this.formatOptions, { max: this.endMax || null });
          if (!okStart) return this.clearInputAndEmitNull();
        }

        if (isMaskedValueComplete(endPart)) {
          const okEnd = isValidMaskedDateString(endPart, this.formatOptions, { max: this.endMax || null });
          if (!okEnd) return this.clearInputAndEmitNull();
        }

        if (isMaskedValueComplete(startPart) && isMaskedValueComplete(endPart)) {
          const okRange = isValidMaskedRangeString(full, this.formatOptions, {
            rangeSeparator: this.rangeSeparator,
            endMax: this.endMax || null,
          });
          if (!okRange) return this.clearInputAndEmitNull();
        }
      };

      this.maskInputEl.addEventListener('input', this.maskInputHandler, true);
      this.maskInputEl.addEventListener('blur', this.maskBlurHandler, true);
    },
    detachMask() {
      if (this.maskInputEl && this.maskInputHandler) {
        this.maskInputEl.removeEventListener('input', this.maskInputHandler, true);
      }
      if (this.maskInputEl && this.maskBlurHandler) {
        this.maskInputEl.removeEventListener('blur', this.maskBlurHandler, true);
      }
      this.maskController = null;
      this.maskInputEl = null;
      this.maskInputHandler = null;
      this.maskBlurHandler = null;
    },
    blockAndClear(e) {
      if (this.maskInputEl) this.maskInputEl.value = '';
      if (this.maskController) this.maskController.update('');

      if (e && typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
      if (e && typeof e.preventDefault === 'function') e.preventDefault();

      this.emitNullRange();
    },
    clearInputAndEmitNull() {
      if (this.maskInputEl) this.maskInputEl.value = '';
      if (this.maskController) this.maskController.update('');
      this.emitNullRange();
    },
    emitNullRange() {
      this.$emit('input', {
        ...(this.value || {}),
        [this.startField]: null,
        [this.endField]: null,
      });
      this.$emit('change');
    },
    onChange() {
      this.$emit('change');
    },
    onClear() {
      this.emitNullRange();
      if (this.maskController) this.maskController.update('');
    },
    onInputError() {
      this.emitNullRange();
      if (this.maskController) this.maskController.update('');
    },
  },
};
</script>
